import { ArrowLeft, Shield, FileText, Clock, Users, AlertTriangle, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background">
      <EnhancedSEO
        title="Terms of Service - Vincent Growth Partners"
        description="Terms and conditions for digital marketing and SEO services. Understand your rights and obligations when working with Vincent Growth Partners."
        keywords="terms of service, digital marketing terms, SEO services agreement, Vincent Growth Partners terms"
        url="/terms-of-service"
      />

      {/* Header */}
      <header className="bg-primary text-primary-foreground py-8">
        <div className="container mx-auto px-6">
          <Link to="/" className="inline-flex items-center space-x-2 text-accent hover:text-accent/80 transition-colors mb-4">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-4xl font-bold">Terms of Service</h1>
          <p className="text-primary-foreground/80 mt-2">Vincent Growth Partners</p>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Agreement Acceptance */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">1. Agreement Acceptance</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>By engaging Vincent Growth Partners for digital marketing, SEO, or growth services, you agree to these terms and conditions.</p>
              <p>These terms constitute a binding agreement between you (the "Client") and Vincent Growth Partners (the "Service Provider").</p>
              <p>If you do not agree to these terms, please do not proceed with our services.</p>
            </div>
          </section>

          {/* Service Delivery */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">2. Service Delivery & Timeline</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>Project timelines are agreed upon before commencement and communicated clearly to all parties.</p>
              <p>While we strive to meet all deadlines, timelines may be adjusted due to client feedback cycles or scope changes.</p>
              <p>Regular progress updates will be provided throughout the project duration.</p>
              <p>Client cooperation and timely feedback are essential for meeting agreed timelines.</p>
            </div>
          </section>

          {/* Payment Terms */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">3. Payment Terms</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>All pricing is agreed in writing before project commencement.</p>
              <p>Payment terms vary by project type: some require upfront payment, others may be invoiced upon completion or in milestones.</p>
              <p>Late payments may incur additional fees and could result in service suspension.</p>
              <p>Refund policies are project-specific and will be outlined in individual service agreements.</p>
            </div>
          </section>

          {/* Client Responsibilities */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">4. Client Responsibilities</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>Clients must provide accurate business information, brand guidelines, and necessary access credentials.</p>
              <p>Timely review and feedback on deliverables is essential for project success.</p>
              <p>Clients are responsible for ensuring all provided content complies with their industry regulations.</p>
              <p>Any third-party tools or platforms required must be accessible to our team when needed.</p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">5. Intellectual Property</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>All content, strategies, and deliverables created specifically for your business become your property upon full payment.</p>
              <p>Pre-existing methodologies, frameworks, and proprietary processes remain the property of Vincent Growth Partners.</p>
              <p>Clients grant permission for case study development and portfolio inclusion, with sensitive information anonymized.</p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">6. Limitation of Liability</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>While we implement best practices, digital marketing results can vary due to market conditions, algorithm changes, and competition.</p>
              <p>Vincent Growth Partners is not liable for indirect damages, lost profits, or business interruption.</p>
              <p>Our total liability is limited to the amount paid for services in the preceding 12 months.</p>
              <p>We maintain professional indemnity insurance and follow industry best practices for risk management.</p>
            </div>
          </section>

          {/* Confidentiality */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">7. Confidentiality & Data Security</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>All client information, business strategies, and data are treated with strict confidentiality.</p>
              <p>We maintain enterprise-grade security measures with 3+ years of cybersecurity experience.</p>
              <p>Data is encrypted, access is controlled, and information is retained securely for 3 years as detailed in our Privacy Policy.</p>
              <p>
                For complete information about data handling, please review our{" "}
                <Link to="/privacy-policy" className="text-primary hover:text-primary/80 underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </section>

          {/* Termination */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">8. Termination</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>Either party may terminate services with 30 days written notice for ongoing projects.</p>
              <p>Immediate termination may occur for breach of terms, non-payment, or violation of agreed policies.</p>
              <p>Upon termination, all completed work will be delivered, and final invoicing will be processed.</p>
            </div>
          </section>

          {/* Governing Law */}
          <section className="bg-card rounded-lg p-8 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground text-primary">9. Governing Law & Disputes</h2>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>These terms are governed by the laws of the jurisdiction where Vincent Growth Partners operates.</p>
              <p>Any disputes will be resolved through good faith negotiation first, followed by mediation if necessary.</p>
              <p>Legal proceedings, if required, will be conducted in the appropriate courts of our jurisdiction.</p>
            </div>
          </section>

          {/* Contact & Updates */}
          <section className="bg-primary/5 rounded-lg p-8 shadow-sm border border-primary/20">
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-bold text-primary">Questions or Updates</h2>
              <p className="text-muted-foreground">
                These terms may be updated periodically. Clients will be notified of significant changes.
              </p>
              <p className="text-muted-foreground">
                For questions about these terms, please contact us directly. We're here to ensure clarity and mutual understanding.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-6">
                <Link 
                  to="/privacy-policy" 
                  className="text-primary hover:text-primary/80 underline font-medium"
                >
                  Privacy Policy
                </Link>
                <Link 
                  to="/content-policy" 
                  className="text-primary hover:text-primary/80 underline font-medium"
                >
                  Content Policy
                </Link>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default TermsOfService;